/**
 * ELIASE BEAUTY — UX Enhancement Scripts
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Companion JavaScript for luxury-ux-enhancements.css
 * 
 * Features:
 * 1. Scroll-triggered reveal animations (IntersectionObserver)
 * 2. Image lazy-load observer
 * 3. Button ripple effect coordinates
 * 4. Sticky ATC visibility toggle
 * 5. Parallax scroll handler (optional)
 * 
 * Performance: Uses IntersectionObserver for efficient scroll detection
 * Compatibility: ES6+, graceful degradation for older browsers
 * 
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  // 1. SCROLL-TRIGGERED REVEAL ANIMATIONS
  // ─────────────────────────────────────────────────────────────────────────
  
  function initRevealObserver() {
    // Skip if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('[data-reveal], .reveal-on-scroll').forEach(el => {
        el.classList.add('is-visible');
      });
      return;
    }

    const revealOptions = {
      root: null,
      rootMargin: '0px 0px -80px 0px', // Trigger 80px before element enters
      threshold: 0.1
    };

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target); // Only animate once
        }
      });
    }, revealOptions);

    // Observe all reveal elements
    document.querySelectorAll('[data-reveal], .reveal-on-scroll').forEach(el => {
      revealObserver.observe(el);
    });

    // Handle staggered children
    document.querySelectorAll('[data-reveal-stagger]').forEach(container => {
      const children = container.children;
      Array.from(children).forEach((child, index) => {
        child.style.transitionDelay = `${index * 80}ms`;
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 2. IMAGE LAZY-LOAD FADE IN
  // ─────────────────────────────────────────────────────────────────────────
  
  function initImageLoadObserver() {
    // For native lazy loading, add 'loaded' class when image completes
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        }, { once: true });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 3. BUTTON RIPPLE EFFECT
  // ─────────────────────────────────────────────────────────────────────────
  
  function initButtonRipple() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.button, .btn, [class*="button"]:not(.button-unstyled)');
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      button.style.setProperty('--ripple-x', `${x}%`);
      button.style.setProperty('--ripple-y', `${y}%`);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 4. STICKY ADD-TO-CART VISIBILITY (MOBILE)
  // ─────────────────────────────────────────────────────────────────────────
  
  function initStickyATC() {
    const stickyBar = document.querySelector('.sticky-add-to-cart, [class*="sticky-atc"], .product-form-sticky');
    const productForm = document.querySelector('form[action*="/cart/add"], .product-form, product-form');
    
    if (!stickyBar || !productForm) return;

    // Only on mobile
    if (window.innerWidth > 749) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // Show sticky bar when form is NOT visible
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: '-100px 0px 0px 0px',
        threshold: 0
      }
    );

    observer.observe(productForm);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 5. PARALLAX SCROLL (OPTIONAL — PERFORMANCE INTENSIVE)
  // ─────────────────────────────────────────────────────────────────────────
  
  function initParallax() {
    // Skip if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    
    const parallaxElements = document.querySelectorAll('.parallax-image');
    if (!parallaxElements.length) return;

    let ticking = false;

    function updateParallax() {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      parallaxElements.forEach(el => {
        const container = el.closest('.parallax-container');
        if (!container) return;

        const rect = container.getBoundingClientRect();
        
        // Only process if visible
        if (rect.bottom < 0 || rect.top > windowHeight) return;

        const progress = (rect.top + rect.height) / (windowHeight + rect.height);
        const translateY = (progress - 0.5) * 60; // ±30px movement

        el.style.transform = `translateY(${translateY}px)`;
      });

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 6. IMAGE REVEAL ANIMATION TRIGGER
  // ─────────────────────────────────────────────────────────────────────────
  
  function initImageReveal() {
    const revealImages = document.querySelectorAll('.image-reveal');
    if (!revealImages.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.2
      }
    );

    revealImages.forEach(el => observer.observe(el));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // INITIALIZE
  // ─────────────────────────────────────────────────────────────────────────
  
  function init() {
    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all elements immediately
      document.querySelectorAll('[data-reveal], .reveal-on-scroll, .image-reveal').forEach(el => {
        el.classList.add('is-visible');
      });
      return;
    }

    initRevealObserver();
    initImageLoadObserver();
    initButtonRipple();
    initStickyATC();
    initImageReveal();
    
    // Parallax is optional and can impact performance
    // Uncomment if needed:
    // initParallax();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section reloads (Theme Editor)
  document.addEventListener('shopify:section:load', init);

})();
